/* EnableNotificationsButton — toggles the user's web-push subscription
 * for THIS device. Hides itself on browsers that don't support push (most
 * notably iOS < 16.4 home-screen apps still miss this).
 *
 * Behaviour:
 *   · Not yet subscribed → "Enable notifications" prompts permission + subscribes
 *   · Already subscribed → "Send test notification" (also offers Disable)
 *   · Permission denied  → instructions to fix in browser settings
 */
import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlB64ToUint8(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function EnableNotificationsButton({ variant = "ghost", className = "" }) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isSupported() || !VAPID_PUBLIC_KEY) return;
    setSupported(true);
    setPermission(Notification.permission);
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((sub) => setSubscribed(!!sub))
    ).catch(() => {});
  }, []);

  if (!supported) return null;

  const subscribe = async () => {
    setBusy(true);
    try {
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error(perm === "denied" ? "Notifications blocked. Allow them in your browser settings to re-enable." : "Permission required to subscribe.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8(VAPID_PUBLIC_KEY),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
      });
      setSubscribed(true);
      toast.success("Notifications enabled on this device");
    } catch (err) {
      // The toast already surfaces a user-friendly message; logging is
      // intentionally skipped in production builds.
      toast.error("Couldn't enable notifications: " + (err?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifications disabled on this device");
    } catch (err) {
      toast.error("Couldn't unsubscribe: " + (err?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) {
        toast.success("Test sent — check for a system notification.");
      } else if (data.skipped === "no_subscriptions") {
        toast.error("No active subscription found. Try toggling off and on.");
      } else {
        toast.error("Push delivery failed; subscription may need refreshing.");
      }
    } catch (err) {
      toast.error("Test failed: " + (err?.response?.data?.detail || err?.message));
    } finally {
      setBusy(false);
    }
  };

  if (subscribed) {
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        <Button
          variant={variant}
          size="sm"
          onClick={sendTest}
          disabled={busy}
          data-testid="push-test-btn"
          className="gap-2 justify-start w-full px-2 py-1.5"
        >
          <BellRing className="h-4 w-4 text-emerald-600" /> Send test notification
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={unsubscribe}
          disabled={busy}
          data-testid="push-unsubscribe-btn"
          className="gap-2 justify-start w-full px-2 py-1.5 text-gray-500"
        >
          <BellOff className="h-4 w-4" /> Disable notifications
        </Button>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div className={`text-xs text-gray-500 px-2 py-1.5 ${className}`}>
        <BellOff className="h-3.5 w-3.5 inline mr-1" /> Notifications blocked — change in your browser settings to re-enable.
      </div>
    );
  }

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={subscribe}
      disabled={busy}
      data-testid="push-subscribe-btn"
      className={`gap-2 justify-start w-full px-2 py-1.5 ${className}`}
    >
      <Bell className="h-4 w-4" /> {busy ? "Enabling…" : "Enable notifications"}
    </Button>
  );
}
